/**
 * NavStack coordinator core (navigation overhaul, chunk A1).
 *
 * The SOLE synchronous authority for the overlay history stack is the in-module
 * `stack` array here (README §3.2). No React state in the hot path. A separate
 * subscription store (§3.2b) drives the cosmetic dynamic z-index repaint only.
 *
 * History is mutated exclusively through the injected imperative `navigate`
 * (spike §3.6: same-path imperative navigate PUSHes; a <Link> would auto-replace
 * — never route a depth-push through a Link). The provider in useNavLayer.tsx
 * injects `navigate` + the current location via refs.
 *
 * Primitives: push (owns its own navigate, dedup no-op never double-navigates),
 * removeToDepth (splice-before-close, the §3.2a load-bearing order that kills
 * the double-pop), requestClose / resetToBase (navigate(-n), popInFlight-guarded),
 * plus synchronous reads (length / has / isTopLayer / indexSnapshot).
 */

export type NavLayer = { key: string; close: () => void };

export const NAV_Z_BASE = 1000;
export const NAV_Z_STEP = 10;

export type NavStackDeps = {
  // RR navigate: (delta) for go(n); (path, { state }) for a push; (path, {
  // replace:true, state }) for the cold-mount normalize. Always imperative.
  navigate: (to: number | string, opts?: { state?: unknown; replace?: boolean }) => void;
  getSamePath: () => string;
  getLocationState: () => Record<string, unknown> | null | undefined;
};

export type NavStack = ReturnType<typeof createNavStack>;

export function createNavStack(getDeps: () => NavStackDeps) {
  const stack: NavLayer[] = [];

  // Dynamic-z subscription store (§3.2b) — separate channel from the hot path.
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const l of listeners) l();
  };

  // Set while an in-app close (requestClose / resetToBase) has issued a
  // navigate(-n) whose echo has not yet landed; blocks a second in-app close
  // during that window (§3.2a L1). Browser-back during the window is still
  // handled normally by the reconciler.
  let popInFlight = false;
  type PendingIntent =
    | { kind: 'close'; key: string }
    | { kind: 'reset' }
    | { kind: 'route'; to: string };
  // More than one close can arrive before the browser echoes the first pop
  // (rapid Back -> Back, Escape during a share close, delete cleanup, etc.).
  // Preserve every intent in arrival order; a target already removed by an
  // earlier, broader close is recognized as satisfied while draining.
  const pendingIntents: PendingIntent[] = [];

  // Self-healing watchdog (added 2026-06-11). popInFlight is cleared ONLY by
  // the reconciler, which runs ONLY when a navigate(-n) actually produces a
  // location change. If the synthetic stack depth ever drifts ahead of real
  // browser history, history.go(-n) exceeds the available entries, fires NO
  // popstate, the reconciler never runs, and popInFlight sticks true forever —
  // every requestClose / resetToBase / resetToBaseThenNavigate then silently
  // no-ops and the app is bricked until a page refresh (observed on the spine
  // companions view, the sole resetToBaseThenNavigate caller). A real
  // history.go popstate lands in well under a frame, so a lock that survives
  // this delay is provably stuck, never merely slow: release it so the app can
  // never permanently deadlock, fire any route the drain failed to deliver, and
  // warn so the underlying depth desync can be pinned and fixed at the root.
  const POP_WATCHDOG_MS = 1000;
  let popWatchdog: ReturnType<typeof setTimeout> | null = null;
  const cancelPopWatchdog = () => {
    if (popWatchdog !== null) {
      clearTimeout(popWatchdog);
      popWatchdog = null;
    }
  };
  const armPopInFlight = () => {
    popInFlight = true;
    cancelPopWatchdog();
    popWatchdog = setTimeout(() => {
      popWatchdog = null;
      if (!popInFlight) return; // reconciler cleared it normally — happy path
      popInFlight = false;
      const stranded = pendingRoute;
      console.warn(
        '[nav-stack] popInFlight watchdog released a stuck lock (navigate(-n) produced no reconcile); stackLen=' +
          stack.length +
          (stranded !== null ? `, stranded route=${stranded}` : ''),
      );
      if (stranded !== null) {
        pendingRoute = null;
        pendingIntents.length = 0;
        getDeps().navigate(stranded);
      } else {
        drainPendingIntents();
      }
    }, POP_WATCHDOG_MS);
  };

  // Route to push AFTER a resetToBaseThenNavigate drain lands. Owned HERE
  // (not by the calling overlay) because the drain unmounts the caller, and
  // React Router silently no-ops a navigate() from an unmounted component's
  // useNavigate — the 2026-06-10 spine "Read chapter lands on home" bug.
  // Consumed by removeToDepth through the provider's always-mounted
  // navigate ref.
  let pendingRoute: string | null = null;

  const issueClose = (key: string): boolean => {
    const index = stack.findIndex((layer) => layer.key === key);
    if (index < 0) return false;
    const count = stack.length - index;
    if (count <= 0) return false;
    armPopInFlight();
    getDeps().navigate(-count);
    return true;
  };

  const issueReset = (): boolean => {
    if (stack.length === 0) return false;
    armPopInFlight();
    getDeps().navigate(-stack.length);
    return true;
  };

  const issueRoute = (to: string) => {
    if (stack.length === 0) {
      getDeps().navigate(to);
      return;
    }
    pendingRoute = to;
    issueReset();
  };

  const drainPendingIntents = () => {
    if (popInFlight) return;
    while (pendingIntents.length > 0) {
      const intent = pendingIntents.shift();
      if (!intent) return;
      if (intent.kind === 'close') {
        if (issueClose(intent.key)) return;
        continue;
      }
      if (intent.kind === 'reset') {
        if (issueReset()) return;
        continue;
      }
      // A route leaves the overlay family, so later queued overlay intents are
      // already satisfied by that departure and must not navigate again.
      pendingIntents.length = 0;
      issueRoute(intent.to);
      return;
    }
  };

  const pushNavDepth = (depth: number) => {
    const { navigate, getSamePath, getLocationState } = getDeps();
    navigate(getSamePath(), {
      state: { ...(getLocationState() ?? {}), navDepth: depth },
    });
  };

  return {
    // --- synchronous reads (hot path) ---
    length: () => stack.length,
    has: (key: string) => stack.some((l) => l.key === key),
    isTopLayer: (key: string) => stack[stack.length - 1]?.key === key,

    // --- dynamic-z subscription (§3.2b) ---
    subscribe: (cb: () => void) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    indexSnapshot: (key: string) => stack.findIndex((l) => l.key === key),

    // --- mutations ---

    /**
     * Append a layer and push one history entry. Dedup-guarded: if `key` is
     * already present, only the stored close target is refreshed and NO
     * navigate fires (so a StrictMode double-invoke or re-render cannot create
     * duplicate entries — LOW 1: push owns its own history write).
     */
    push(key: string, close: () => void) {
      const existing = stack.find((l) => l.key === key);
      if (existing) {
        existing.close = close;
        return;
      }
      stack.push({ key, close });
      notify();
      pushNavDepth(stack.length);
    },

    /**
     * Reconciler BACK row: remove every layer at index >= browserDepth.
     * Splice FIRST, then fire each removed layer's close() top-down, so a
     * close() that flips its surface closed re-enters the layer's cleanup to
     * find its key already gone (membership) and no-ops (kills B1 double-pop).
     */
    removeToDepth(browserDepth: number) {
      if (browserDepth >= stack.length) return;
      const removed = stack.splice(browserDepth).reverse(); // top-first
      notify();
      for (const l of removed) l.close();
      // Deep-link completion: once the drain reaches base, push the route
      // queued by resetToBaseThenNavigate. By this point every removed
      // layer is deregistered (splice-before-close), so the forward route
      // swap unmounts overlay components whose cleanups find their keys
      // gone and no-op — no unmount-vs-pop duel. Navigating inside the
      // reconciler's layout effect is the cold-mount-normalize precedent.
      if (stack.length === 0 && pendingRoute !== null) {
        const to = pendingRoute;
        pendingRoute = null;
        getDeps().navigate(to);
      }
    },

    /**
     * User/in-app close of `key`: move history back by the number of layers at
     * or above `key`; the reconciler's BACK row then does the actual removal.
     */
    requestClose(key: string) {
      if (popInFlight) {
        pendingIntents.push({ kind: 'close', key });
        return;
      }
      issueClose(key);
    },

    /** Unwind every synthetic entry in one tick (handleNew). */
    resetToBase() {
      if (popInFlight) {
        pendingIntents.push({ kind: 'reset' });
        return;
      }
      issueReset();
    },

    /**
     * Deep-link OUT of the overlay stack: unwind every layer, then push
     * `to` once the drain lands (removeToDepth consumes the queued route).
     * The forward push fires through the provider's navigate ref, NEVER
     * from the calling overlay — the drain unmounts the caller, and an
     * unmounted component's useNavigate is a silent React Router no-op.
     * Dormant since the tap-to-match cut (rule #79) removed its only
     * caller; kept as a rule-#78 escape hatch for any future overlay that
     * deep-links to a route. With an empty stack this is a plain navigate.
     */
    resetToBaseThenNavigate(to: string) {
      if (popInFlight) {
        pendingIntents.push({ kind: 'route', to });
        return;
      }
      issueRoute(to);
    },

    // --- popInFlight lifecycle (driven by the reconciler) ---
    isPopInFlight: () => popInFlight,
    clearPopInFlight: () => {
      popInFlight = false;
      cancelPopWatchdog();
      drainPendingIntents();
    },

    // --- cold-mount only ---
    resetStack: () => {
      stack.length = 0;
      pendingRoute = null;
      pendingIntents.length = 0;
      popInFlight = false;
      cancelPopWatchdog();
      notify();
    },
  };
}
